import PersonRounded from "@mui/icons-material/PersonRounded";
import {
	Button,
	Chip,
	Dialog,
	DialogActions,
	DialogContent,
	DialogTitle,
	Stack,
	TextField,
	Typography,
} from "@mui/material";
import {
	createContext,
	type ReactNode,
	useCallback,
	useContext,
	useEffect,
	useRef,
	useState,
} from "react";
import { Controller, useForm } from "react-hook-form";
import { useScanBurst } from "@/pages/sawlines/hooks/useScanBurst";

const CURRENT_KEY = "sawTablet.employee";
const RECENT_KEY = "sawTablet.recentEmployees";

type Action = (employee: string) => void;
type Stored = { number: string; shiftId: number | null; login?: number };

const read = <T,>(key: string): T | null => {
	try {
		const raw = localStorage.getItem(key);
		return raw ? (JSON.parse(raw) as T) : null;
	} catch {
		return null;
	}
};
const write = (key: string, value: unknown) => {
	try {
		localStorage.setItem(key, JSON.stringify(value));
	} catch {
		// no storage: number lives until reload
	}
};

// mesf-core writes userMESData at login and drops it at logout
const loginId = () => {
	let hash = 0;
	for (const c of JSON.stringify(read("userMESData")))
		hash = (Math.imul(hash, 31) + c.charCodeAt(0)) | 0;
	return hash;
};

const EmployeeContext = createContext<{
	employee: string | null;
	requireEmployee: (action: Action) => void;
	changeEmployee: () => void;
}>({
	employee: null,
	requireEmployee: () => {},
	changeEmployee: () => {},
});

export const useEmployeeNumber = () => useContext(EmployeeContext);

// Remembered per tablet until the shift changes or someone logs in again.
export const EmployeeNumberProvider = ({
	shiftId,
	children,
}: {
	// null/undefined = shift unknown: number still usable
	shiftId: number | null | undefined;
	children: ReactNode;
}) => {
	const [stored, setStored] = useState(() => read<Stored>(CURRENT_KEY));
	const [login] = useState(loginId);
	const [recent, setRecent] = useState(() => read<string[]>(RECENT_KEY) ?? []);
	const [open, setOpen] = useState(false);
	const pending = useRef<Action | null>(null);
	const shift = shiftId ?? null;
	const employee =
		stored &&
		stored.login === login &&
		(shift === null || stored.shiftId === null || stored.shiftId === shift)
			? stored.number
			: null;

	// number entered before the shift loaded belongs to the first shift seen
	useEffect(() => {
		if (shift === null || !stored || stored.shiftId !== null) return;
		const next = { ...stored, shiftId: shift };
		setStored(next);
		write(CURRENT_KEY, next);
	}, [shift, stored]);

	const requireEmployee = useCallback(
		(action: Action) => {
			if (employee) return action(employee);
			pending.current = action;
			setOpen(true);
		},
		[employee],
	);
	const changeEmployee = useCallback(() => {
		pending.current = null;
		setOpen(true);
	}, []);

	const close = (number: string | null) => {
		setOpen(false);
		const action = pending.current;
		pending.current = null;
		if (!number) return;
		const next = { number, shiftId: shift, login };
		const nextRecent = [number, ...recent.filter((n) => n !== number)].slice(
			0,
			3,
		);
		setStored(next);
		setRecent(nextRecent);
		write(CURRENT_KEY, next);
		write(RECENT_KEY, nextRecent);
		action?.(number);
	};

	return (
		<EmployeeContext.Provider
			value={{ employee, requireEmployee, changeEmployee }}
		>
			{children}
			{open && (
				<EmployeeDialog
					initial={employee ?? ""}
					recent={recent}
					onClose={close}
				/>
			)}
		</EmployeeContext.Provider>
	);
};

export const EmployeeField = () => {
	const { employee, changeEmployee } = useEmployeeNumber();
	return (
		<Button
			onClick={changeEmployee}
			color={employee ? "primary" : "warning"}
			variant="outlined"
			startIcon={<PersonRounded />}
			sx={{
				height: 48,
				px: 2,
				flexShrink: 0,
				borderRadius: 6,
				gap: 1.5,
				color: "text.primary",
				bgcolor: employee ? "#eef3ee" : "background.paper",
			}}
		>
			{employee ? (
				<Typography component="span" fontSize={19} fontWeight={700}>
					{employee}
				</Typography>
			) : (
				"Employee #"
			)}
			<Typography
				component="span"
				variant="button"
				color={employee ? "primary" : "warning.dark"}
			>
				{employee ? "Change" : "Enter"}
			</Typography>
		</Button>
	);
};

const EmployeeDialog = ({
	initial,
	recent,
	onClose,
}: {
	initial: string;
	recent: string[];
	onClose: (number: string | null) => void;
}) => {
	const {
		control,
		handleSubmit,
		setValue,
		formState: { isValid },
	} = useForm({ defaultValues: { number: initial }, mode: "onChange" });
	const scanBurst = useScanBurst();
	const beforeBurst = useRef(initial);

	return (
		<Dialog open maxWidth="xs" fullWidth onClose={() => onClose(null)}>
			<form onSubmit={handleSubmit(({ number }) => onClose(number))}>
				<DialogTitle>Employee #</DialogTitle>
				<DialogContent dividers>
					<Controller
						name="number"
						control={control}
						rules={{
							required: "Employee # is required",
							maxLength: { value: 20, message: "Up to 20 characters" },
							pattern: {
								value: /^[A-Za-z0-9]+$/,
								message: "Letters or digits only",
							},
						}}
						render={({ field, fieldState: { error } }) => (
							<TextField
								autoFocus
								fullWidth
								label="Employee #"
								value={field.value}
								onChange={field.onChange}
								onBlur={field.onBlur}
								inputRef={field.ref}
								// a tag scanned here is not an employee #: drop the whole burst and its Enter
								onKeyDown={(e) => {
									if (!scanBurst(e)) beforeBurst.current = field.value;
									else {
										e.preventDefault();
										setValue("number", beforeBurst.current, {
											shouldValidate: true,
										});
									}
								}}
								error={!!error}
								helperText={error?.message ?? "Letters or digits, up to 20"}
								inputProps={{ autoComplete: "off", spellCheck: false }}
								sx={{ "& input": { fontSize: 24, fontWeight: 600 } }}
							/>
						)}
					/>
					{recent.length > 0 && (
						<Stack direction="row" gap={1} sx={{ mt: 2 }} flexWrap="wrap">
							{recent.map((number) => (
								<Chip
									key={number}
									label={`#${number}`}
									onClick={() => onClose(number)}
									sx={{ height: 48, fontSize: 18, px: 1 }}
								/>
							))}
						</Stack>
					)}
				</DialogContent>
				<DialogActions sx={{ p: 2, gap: 1, "& button": { minHeight: 52 } }}>
					<Button
						color="inherit"
						variant="outlined"
						onClick={() => onClose(null)}
					>
						Cancel
					</Button>
					<Button type="submit" variant="contained" disabled={!isValid}>
						Save
					</Button>
				</DialogActions>
			</form>
		</Dialog>
	);
};
